'use client';

import React from 'react';
import { Film, Tv2, Clapperboard, CheckSquare, XSquare, RefreshCw, Package } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Props {
  activeTab: string;
  onTabChange: (tab: string) => void;
  seriesCount: number;
  moviesCount: number;
  totalCount: number;
  selectedCount: number;
  filteredCount: number;
  customCount?: number;
  onSelectAll: () => void;
  onClearAll: () => void;
}

export default function MediaLibraryHeader({
  activeTab,
  onTabChange,
  seriesCount,
  moviesCount,
  totalCount,
  selectedCount,
  filteredCount,
  customCount = 0,
  onSelectAll,
  onClearAll,
}: Props) {
  const tabs = [
    { key: 'all', label: 'All Media', icon: Film, count: totalCount },
    { key: 'series', label: 'Series', icon: Tv2, count: seriesCount },
    { key: 'movies', label: 'Movies', icon: Clapperboard, count: moviesCount },
    { key: 'custom', label: 'Custom', icon: Package, count: customCount },
  ];

  return (
    <div className="sticky top-0 z-20 border-b border-border px-6 xl:px-8 2xl:px-10 py-4"
      style={{ background: 'var(--background)' }}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Title + counts */}
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Media Library</h1>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono-data">
              {totalCount} items · {seriesCount} series · {moviesCount} movies{customCount > 0 ? ` · ${customCount} custom` : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary text-primary-foreground font-mono-data">
              {selectedCount} selected
            </span>
          )}
          <button
            onClick={onSelectAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium btn-ghost"
          >
            <CheckSquare size={14} />
            Select all ({filteredCount})
          </button>
          {selectedCount > 0 && (
            <button
              onClick={onClearAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium btn-ghost text-danger"
            >
              <XSquare size={14} />
              Clear
            </button>
          )}
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium btn-ghost"
            title="Refresh libraries from Sonarr & Radarr"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mt-3">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={`tab-${tab.key}`}
              onClick={() => onTabChange(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                isActive
                  ? 'sidebar-item-active' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon size={15} />
              {tab.label}
              <span
                className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full font-mono-data ${
                  isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}